import { ViewIds } from '@ardoq/api-types';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import {
  ExportHandler,
  ExportViewEvent,
  getExportsForHtmlView,
  getExportsForSvgView,
} from '@ardoq/export';
import type { I18nLocale } from '@ardoq/i18n';
import { SettingsConfig, viewSettingsConsts } from '@ardoq/view-settings';
import { getRightMenuConfig, ViewSettingsBase } from '@ardoq/settings-bar';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { isTruthy } from '@ardoq/common-helpers';
import { isPresentationMode } from 'env';
import { getExportLabels } from 'views/getExportLabels';

export type ViewIdsWithExportHandlers = ViewIds.BUBBLE | ViewIds.TIMELINE;
export const hasExportHandler = (
  viewId: ViewIds
): viewId is ViewIdsWithExportHandlers =>
  viewId === ViewIds.BUBBLE || viewId === ViewIds.TIMELINE;

const getExportHandler = (
  viewId: ViewIdsWithExportHandlers
): ExportHandler<HTMLElement> => {
  switch (viewId) {
    case ViewIds.BUBBLE:
      return getExportsForSvgView;
    case ViewIds.TIMELINE:
      return getExportsForHtmlView;
    // Add all relevant ViewIds here and in ViewIdsWithExportHandlers to ensure exhaustiveness

    default: {
      // Exhaustiveness check
      const _exhaustiveCheck: never = viewId;
      throw new Error(
        `No export handler defined for viewId: ${_exhaustiveCheck}`
      );
    }
  }
};

export type GetRightSettingsMenuConfigForWorkspaceModeArgs<
  TViewSettings extends ViewSettingsBase,
> = {
  viewSettings: TViewSettings;
  viewId: ViewIdsWithExportHandlers;
  viewInstanceId: string;
  getContainer: () => HTMLElement | null;
  viewHasLegend: (viewId: ViewIds) => boolean;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  trackingFunction:
    | (() => void)
    | ((metadata: ExportViewEvent) => Promise<void>);
  getStyleSheets: (() => Promise<any>) | (() => CSSStyleSheet[]);
  isEmptyView: boolean;
  addToPresentation: (name: ViewIds, isViewpointMode: boolean) => void;
  knowledgeBaseLink: KnowledgeBaseLink;
  /**
   * contains the today button and zoom controls, provided by view context
   */
  zoomControls: SettingsConfig[];
  i18nLocale: I18nLocale;
};
export const getRightSettingsMenuConfig = <
  TViewSettings extends ViewSettingsBase,
>({
  viewSettings,
  viewId,
  viewHasLegend,
  onViewSettingsUpdate,
  getContainer,
  trackingFunction,
  getStyleSheets,
  isEmptyView,
  addToPresentation,
  zoomControls,
  knowledgeBaseLink,
  viewInstanceId,
  i18nLocale,
}: GetRightSettingsMenuConfigForWorkspaceModeArgs<TViewSettings>): SettingsConfig[] => {
  const exportHandler = getExportHandler(viewId);

  const exports = exportHandler({
    container: getContainer,
    exportedViewMetadata: {
      name: viewId,
      viewInstanceId,
      isViewpointMode: false,
      isDiscoverViewpoint: false,
    },
    trackingFunction,
    addToPresentation,
    showPresentationOption: true,
    getStyleSheets,
    labels: getExportLabels(i18nLocale),
  });

  const isPresentationModeActive = isPresentationMode();

  return [
    ...zoomControls,
    ...getRightMenuConfig({
      viewId: viewId,
      viewstate: viewSettings,
      exports: {
        ...exports,
        isDisabled: isEmptyView,
      },
      withLegend: viewHasLegend(viewId) && !isPresentationModeActive,
      knowledgeBaseLink,
      onViewSettingsUpdate,
      isFullscreenDisabled: isPresentationModeActive,
      isPresentationMode: isPresentationModeActive,
      legendOnClick: () => {
        onViewSettingsUpdate(
          viewId,
          {
            [viewSettingsConsts.IS_LEGEND_ACTIVE]:
              !viewSettings[viewSettingsConsts.IS_LEGEND_ACTIVE],
          },
          !isPresentationModeActive
        );
      },
      i18nLocale,
    }),
  ].filter(isTruthy);
};
